
import React from 'react';
import { Check, Clock, DollarSign, TrendingUp, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';

const WhyUs = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  const reasons = [
    {
      title: t.costSaving,
      description: t.costSavingDesc,
      icon: DollarSign
    },
    {
      title: t.timeSaving,
      description: t.timeSavingDesc,
      icon: Clock
    },
    {
      title: t.increaseSales,
      description: t.increaseSalesDesc,
      icon: TrendingUp
    },
    {
      title: t.targetAudience,
      description: t.targetAudienceDesc,
      icon: Users
    }
  ];

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" id="why-us">
      {/* Video background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <iframe 
            allow="fullscreen;autoplay" 
            allowFullScreen 
            src="https://streamable.com/e/j9pfg4?autoplay=1&muted=1&nocontrols=1&loop=1" 
            width="100%" 
            height="100%" 
            style={{
              border: "none", 
              width: "100%", 
              height: "100%", 
              position: "absolute", 
              left: "0px", 
              top: "0px", 
              overflow: "hidden",
              pointerEvents: "none",
              objectFit: "cover",
              filter: "brightness(1.2) contrast(1.1)" // Enhance video visibility
            }}
            title="Why Us Background Video"
          ></iframe>
        </div>
        
        {/* Overlay for better content readability */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] z-0"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white relative inline-block">
            <span className="relative z-10">{t.whyFutureLevel}</span>
            <span className="absolute -bottom-2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-purple-400/70 to-transparent"></span>
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto mt-6">
            {t.whyFutureLevelSubtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-16">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            
            return (
              <div 
                key={index} 
                className="flex space-x-4 group backdrop-blur-sm bg-black/30 p-6 rounded-xl border border-purple-500/20 hover:-translate-y-1 transition-transform duration-300"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="shrink-0">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center relative overflow-hidden group-hover:bg-white/30 transition-colors duration-300">
                    <Icon size={24} className="text-white relative z-10" />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-purple-700/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white flex items-center font-future micro-glitch">
                    <Check size={20} className="text-green-400 mr-2" /> {reason.title}
                  </h3>
                  <p className="text-gray-200 relative">
                    {reason.description}
                    <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-transparent via-purple-400/30 to-transparent group-hover:w-full transition-all duration-500"></span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
